/**
 * What this module can propose — the means-test levers that do NOT involve moving house.
 *
 * THE BOUNDARY, and it is deliberate. Selling, downsizing, a retirement village and a granny flat
 * each already have a module with their own explainers, timing rules and traps. Reimplementing any
 * of them here would give the app five places that model a home sale, and they would drift. So this
 * engine owns the levers that leave the client exactly where they are, and the module links out for
 * the moves.
 *
 * Every amount here is SIZED FROM WHAT THE CLIENT ACTUALLY HAS. A candidate proposing a $300,000
 * annuity to someone with $80,000 in the bank is not a strategy, and an engine that generates one
 * has to be second-guessed by the reader — which defeats the point of it.
 */
import { debtsWorthTesting, ZERO_INTEREST_DEBT_FLOOR } from '../../strategy/debtsToTest.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed } from '../../../../shared/src/calculation/annuityPreference.ts';
import { formatCurrency } from '../../utils/index.ts';
import { annuitySizesFor } from '../../strategy/annuitySizes.ts';
import { quotedIncomeFor, type AnnuityQuote } from '../../strategy/annuityQuote.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { allocateNonConcessionalContribution, nonConcessionalCapacityForHousehold } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import { manualDebtPayoff, payableDebtsForCashflow } from './cashflowPensionScenario.ts';
import {
    FUNERAL_BOND_LIMIT,
    GIFTING_LIMIT_ANNUAL,
} from '../../constants/index.ts';

export interface CashflowPensionCandidate {
    id: string;
    name: string;
    description: string;
    changeNothing?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
    annuity?: Record<string, unknown>;
    /** Which assets pay for it. Without this the shared levers do nothing at all. */
    fundingSources?: Array<{ type: string; id: string; amountToTake: number }>;
    /** An after-tax super contribution, per person (the engine caps each at what the rules allow). */
    superContribution?: { client?: number; partner?: number; commenceIncomeStream?: boolean };
    /** An investment (insurance) bond to this value. */
    investmentBond?: number;
    debtPayoff?: Record<string, unknown>;
}

const amountOf = (v: unknown): number => Math.max(0, parseFloat(String(v ?? '')) || 0);

/** A named pot the levers can actually draw from. */
interface LiquidSource {
    type: string;
    id: string;
    value: number;
}

/**
 * Cash and near-cash the client could actually move — not the house, not their super pension.
 *
 * Returned as SOURCES, not a total, because the shared levers need to be told which asset pays.
 * Given only an amount they liquidate nothing and apply nothing (see cashflowPension.ts's
 * applyCandidate), which produces a ranking of identical candidates rather than an error.
 *
 * Bank accounts first: spending cash costs nothing, whereas selling shares can crystallise CGT that
 * this module does not model and should not silently incur.
 */
function liquidSources(factFind: DraftFactFind): LiquidSource[] {
    const assets: any = factFind?.assets || {};
    const collect = (type: string) =>
        (assets[type] || [])
            .filter((row: any) => row?.id && amountOf(row?.value) > 0)
            .map((row: any) => ({ type, id: String(row.id), value: amountOf(row.value) }));
    return [...collect('bankAccounts'), ...collect('termDeposits'), ...collect('shares')];
}

function liquidAssets(factFind: DraftFactFind): number {
    return liquidSources(factFind).reduce((sum, source) => sum + source.value, 0);
}

/**
 * Draw `amount` across the available sources, largest-first within the bank-before-shares order.
 *
 * Keeps the emergency reserve intact by only ever being called with a committable amount.
 */
function fundingFor(sources: LiquidSource[], amount: number): Array<{ type: string; id: string; amountToTake: number }> {
    const plan: Array<{ type: string; id: string; amountToTake: number }> = [];
    let remaining = amount;
    for (const source of sources) {
        if (remaining <= 0) break;
        const take = Math.min(source.value, remaining);
        if (take <= 0) continue;
        plan.push({ type: source.type, id: source.id, amountToTake: take });
        remaining -= take;
    }
    return plan;
}

function isCouple(factFind: DraftFactFind): boolean {
    return factFind?.adviceFor === 'couple';
}

/**
 * A reserve the engine will not propose spending.
 *
 * Someone paying for help at home needs money they can reach this month. Every candidate here locks
 * capital away to some degree — a gift is gone, a bond is untouchable until death, an annuity is
 * committed for life — so proposing one that leaves nothing accessible would improve a means test
 * and damage the client.
 */
export const EMERGENCY_RESERVE = 50_000;

/*
 * THE CLIENT'S OWN ANSWERS (cashflow rebuild, 21 Oct 2026 — step 3's questions, as the village's). How much
 * stays in the bank for emergencies (goals.cpEmergencyReserve; $50,000 when not answered, the figure this
 * engine always used, so an unanswered file tests exactly the plans it did before), and which levers they
 * would consider (goals.cpLevers). Leaving a lever out only removes its plans: the arithmetic of the plans
 * that remain is untouched.
 */
export type CashflowLever = 'bond' | 'gift' | 'tax';
export const CASHFLOW_LEVERS: readonly CashflowLever[] = ['bond', 'gift', 'tax'];

/** The money kept in the bank for emergencies when money is moved: the client's answer, or $50,000. */
export function cashflowReserveOf(factFind: DraftFactFind | null | undefined): number {
    const v = (factFind?.goals as { cpEmergencyReserve?: unknown } | undefined)?.cpEmergencyReserve;
    const n = v === '' || v == null ? NaN : Number(v);
    return Number.isFinite(n) && n >= 0 ? n : EMERGENCY_RESERVE;
}

/** The levers the client would consider; all three until they say otherwise. */
export function cashflowLeversOf(factFind: DraftFactFind | null | undefined): CashflowLever[] {
    const raw = (factFind?.goals as { cpLevers?: unknown } | undefined)?.cpLevers;
    if (typeof raw !== 'string') return [...CASHFLOW_LEVERS];
    return raw.split(',').map((x) => x.trim()).filter((x): x is CashflowLever => (CASHFLOW_LEVERS as readonly string[]).includes(x));
}

/** What is available to commit, after keeping the reserve back. */
function committable(factFind: DraftFactFind): number {
    return Math.max(0, liquidAssets(factFind) - cashflowReserveOf(factFind));
}

/**
 * THE MONEY A GUARANTEED INCOME COULD COME FROM, AS THE OPTIONS SEE IT (owner, 21 Oct 2026) — and as the
 * guaranteed-income question shows it, so the share the client chooses is a share of the same figure the
 * options are sized against: cash, term deposits and shares above the emergency money.
 */
export function cashflowAnnuityInvestable(factFind: DraftFactFind): { amount: number; reserve: number; madeOf: string } {
    const sources = liquidSources(factFind);
    const has = (type: string) => sources.some((s) => s.type === type);
    const parts = [has('bankAccounts') ? 'bank accounts' : null, has('termDeposits') ? 'term deposits' : null, has('shares') ? 'shares' : null].filter(Boolean) as string[];
    const madeOf = parts.length <= 1 ? (parts[0] || 'savings') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    return { amount: committable(factFind), reserve: cashflowReserveOf(factFind), madeOf };
}

export function generateCashflowPensionCandidates(
    factFind: DraftFactFind,
    /**
     * A provider's quote, where the client has one.
     *
     * Threaded into the annuity candidates rather than applied to the winner afterwards: a real
     * quote can change WHICH option wins, and a ranking struck on our indicative rate would then be
     * the wrong ranking (owner, 2026-09-01).
     *
     * A QUOTE BELONGS TO ONE TYPE AND ONE AMOUNT (owner, 21 Oct 2026 — the guaranteed income review M5;
     * annuityQuote.ts). Candidates of the same type use it scaled to their own amount; the other types keep
     * our estimate. It used to replace the income on every annuity candidate, whatever its type.
     */
    annuityQuote: AnnuityQuote | null = null,
): CashflowPensionCandidate[] {
    const candidates: CashflowPensionCandidate[] = [
        {
            /*
             * THE BENCHMARK, and it is always first. Everything else is stated relative to it. An
             * engine with no do-nothing option ranks actions only against each other, so a strategy
             * that leaves the family worse off can still be crowned "best" — the exact defect the
             * Downsizer engine was caught with (2026-08-26).
             */
            id: 'cp-nothing',
            name: 'Change nothing',
            description: 'Your position as it stands, projected over the same period. Every option below is measured against this.',
            changeNothing: true,
        },
    ];

    const sources = liquidSources(factFind);
    const available = committable(factFind);
    const people = isCouple(factFind) ? 2 : 1;
    const levers = cashflowLeversOf(factFind);
    const bondsOk = levers.includes('bond');
    const giftOk = levers.includes('gift');
    const taxOk = levers.includes('tax');

    /*
     * FUNERAL BONDS — the cleanest of the levers and the smallest. Fully exempt up to the published
     * limit per person, and the money buys something most families pay for anyway. Sized at the
     * limit exactly: below it leaves exemption unused, above it is counted like any other asset, so
     * proposing more would be proposing a cost with no benefit.
     */
    const bondTotal = FUNERAL_BOND_LIMIT * people;
    if (bondsOk && available >= bondTotal) {
        candidates.push({
            id: 'cp-funeral-bond',
            name: `Funeral bond${people > 1 ? 's' : ''} — ${people > 1 ? 'one each' : 'up to the exempt limit'}`,
            description:
                `Move ${people > 1 ? 'up to the exempt limit each' : 'up to the exempt limit'} into a funeral bond. It stops counting for the assets test entirely, and it pays for something your family would otherwise fund from the estate.`,
            funeralBondPurchase: bondTotal,
            fundingSources: fundingFor(sources, bondTotal),
        });
    }

    /*
     * GIFTING — within the yearly free area only. Above it the excess is still counted, and still
     * deemed to earn income, for five years under the deprivation rules: the money has gone but the
     * means test has not noticed. The engine cannot weigh that five-year cost against a family's own
     * reasons for a larger gift, so it does not try.
     */
    if (giftOk && available >= GIFTING_LIMIT_ANNUAL) {
        candidates.push({
            id: 'cp-gift',
            name: 'Gift within the yearly free area',
            description:
                'Give away up to the yearly free area. Inside that limit it leaves the assets test straight away — above it, the excess is still counted for five years even though the money is gone.',
            giftingAmount: GIFTING_LIMIT_ANNUAL,
            fundingSources: fundingFor(sources, GIFTING_LIMIT_ANNUAL),
        });
    }

    /* Both together, where there is enough to do both without breaking the reserve. */
    if (bondsOk && giftOk && available >= bondTotal + GIFTING_LIMIT_ANNUAL) {
        candidates.push({
            id: 'cp-bond-and-gift',
            name: 'Funeral bond and a gift',
            description: 'Both of the above together — the largest reduction available without committing capital for life.',
            funeralBondPurchase: bondTotal,
            giftingAmount: GIFTING_LIMIT_ANNUAL,
            fundingSources: fundingFor(sources, bondTotal + GIFTING_LIMIT_ANNUAL),
        });
    }

    /*
     * LIFETIME ANNUITY — the biggest lever and the one with the sharpest trade-off. Only 60% of the
     * purchase price counts as an asset, so it moves far more than a bond can, but the capital is
     * committed for life and cannot be recovered. Offered only where a meaningful amount is
     * available after the reserve; below that the loss of access buys too little to justify itself.
     */
    /*
     * HOW MUCH (owner, 21 Oct 2026 — the village review's decision 11, every module; annuitySizes.ts): up to a
     * quarter of what is available, and — where it would lift the Age Pension — the amount that lifts it most,
     * as a plan of its own. Previously half of what was available, up to $400,000.
     */
    const MIN_ANNUITY = 50_000;
    const sizes = annuitySizesFor(factFind, { investable: available, capPct: 25, homeowner: Number(factFind?.home?.value) > 0, lessCounted: available });
    for (const size of sizes) {
        if (size.amount < MIN_ANNUITY) continue;
        const rounded = size.pensionMax ? size.amount : Math.floor(size.amount / 10_000) * 10_000;
        const tail = size.pensionMax ? '-max' : '';
        const sizeName = size.pensionMax ? ', the amount that lifts the Age Pension most' : '';
        /*
         * BOTH ENDS OF THE RESIDUAL-CAPITAL TRADE-OFF.
         *
         * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its residual
         * capital value — RCV changes the payment rate, not the assets-test treatment. But net worth
         * values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately), so offering only
         * the nil-RCV version meant every annuity option here lost the estate comparison by its full
         * purchase price on day one. It was ruled out before the comparison started, in a module
         * whose whole purpose is comparing the estate cost of improving cashflow and pension.
         *
         * Every other module offers both. This one was built after that fix went in and inherited
         * the original single-variant shape.
         */
        const annuityMeansTest = 'Only 60% of what you pay counts in the assets test, so it moves more than any other option here';
        const cpAnnuityPreference = annuityPreferenceOf(factFind as never);
        if (allowsResidual(cpAnnuityPreference, 0)) candidates.push({
            id: `cp-annuity${tail}`,
            name: `Lifetime annuity (nil residual${sizeName})`,
            description:
                `Buy a lifetime income stream. ${annuityMeansTest} — but the money is committed for life and cannot be taken back. This version pays the most income and leaves nothing of the purchase price to your estate.`,
            annuity: { purchaseAmount: rounded, annuityType: 'lifetime', ownership: 'client', residualCapital: 0, incomeOverride: quotedIncomeFor(annuityQuote, 'lifetime', 0, rounded) ?? null },
            fundingSources: fundingFor(sources, rounded),
        });
        if (allowsResidual(cpAnnuityPreference, 1)) candidates.push({
            id: `cp-annuity${tail}-rcv`,
            name: `Lifetime annuity (capital returned${sizeName})`,
            description:
                `The same annuity, but with the purchase price coming back to the estate on an early death — in full within the first half of life expectancy, less after that — rather than kept by the provider. It pays less income, and Centrelink still counts only 60% either way.`,
            annuity: { purchaseAmount: rounded, annuityType: 'lifetime', ownership: 'client', residualCapital: 1, incomeOverride: quotedIncomeFor(annuityQuote, 'lifetime', 100, rounded) ?? null },
            fundingSources: fundingFor(sources, rounded),
        });
        if (allowsGuaranteed(cpAnnuityPreference, factFind as never)) candidates.push({
            id: `cp-annuity${tail}-guaranteed`,
            name: `Lifetime annuity (full amount guaranteed${sizeName})`,
            description:
                `A lifetime annuity and a life policy bought together, so the full purchase price comes back to your estate whenever death comes. It pays the least income of the three, and only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost.`,
            annuity: { purchaseAmount: rounded, annuityType: 'guaranteed', ownership: 'client', residualCapital: 1, incomeOverride: quotedIncomeFor(annuityQuote, 'guaranteed', 100, rounded) ?? null },
            fundingSources: fundingFor(sources, rounded),
        });
    }

    /*
     * AFTER-TAX SUPER (F.6) AND AN INVESTMENT BOND (F.5) — 17 Sep 2026.
     *
     * Neither moves the means test on its own — accumulation super for someone over Age Pension age,
     * a pension, and a bond are all counted and deemed like the cash they replace. What they change
     * is TAX: 15% (or nil in a pension) or the bond's 30% inside, instead of the client's own rate on
     * the same earnings. The exception is worth the most: a couple with one member under Age Pension
     * age, whose accumulation super Centrelink does not count until they reach it.
     *
     * Logic-based: super only where the person may contribute (under 75, room under the balance
     * bands); the bond only where they may not — offering both to the same person would rank a
     * 30% tax rate against a 15% one and call it a choice.
     */
    if (taxOk && available >= MIN_ANNUITY) {
        const ff = factFind as unknown as FactFind;
        const superRoom = nonConcessionalCapacityForHousehold(ff);
        if (superRoom.total > 0) {
            const ncc = allocateNonConcessionalContribution(ff, Math.round(available / 10_000) * 10_000);
            const total = ncc.client + ncc.partner;
            if (total >= MIN_ANNUITY) {
                candidates.push({
                    id: 'cp-super-after-tax',
                    name: 'Move savings into super (after-tax contribution)',
                    description: `Contribute ${formatCurrency(total)} of your savings to super after tax${ncc.preferred ? ` — ${ncc.preferred === 'client' ? (factFind?.client?.firstName || 'the client') : (factFind?.partner?.firstName || 'the partner')} first, whose accumulation super is not counted by Centrelink until Age Pension age` : ', where the earnings are taxed at 15% (nil once it pays a pension) instead of at your own rate'}. Capped at what each of you may contribute now; the money stays yours and can be drawn on.`,
                    superContribution: { client: ncc.client, partner: ncc.partner, commenceIncomeStream: ncc.preferred == null },
                    fundingSources: fundingFor(sources, total),
                });
            }
        } else {
            const bondAmount = Math.round(Math.min(available * 0.5, 400_000) / 10_000) * 10_000;
            if (bondAmount >= MIN_ANNUITY) {
                candidates.push({
                    id: 'cp-invest-bond',
                    name: 'Move savings into an investment bond',
                    description: `Put ${formatCurrency(bondAmount)} of your savings into an investment bond — the tax-paid home for money super can no longer take. It pays its own tax inside (30%) and nothing on your tax return; after ten years withdrawals are tax-free. Centrelink counts it in full and deems it, like the cash it replaces, so this is about tax, not the pension. Best left alone for ten years.`,
                    investmentBond: bondAmount,
                    fundingSources: fundingFor(sources, bondAmount),
                });
            }
        }
    }

    candidates.push(...clearDebtTwins(factFind, candidates, sources));
    return candidates;
}

/** Below this, INTEREST-FREE debts are not tested cleared beside each plan (strategy/debtsToTest.ts). */
export const CP_DEBT_TWIN_FLOOR = ZERO_INTEREST_DEBT_FLOOR;

/**
 * EVERY PLAN, ALSO WITH THE DEBTS CLEARED (owner, 22 Oct 2026: "every module needs to test paying it off").
 *
 * Paying off a debt was only possible by hand here. Now, as in the village, downsizing and granny flat, each
 * plan has a twin that does the same and then pays off every debt (not a reverse mortgage), dearest first, from
 * what the plan leaves, keeping the emergency money in the bank; "change nothing" has one too, "pay off the
 * debts" on its own. The same `debtPayoff` the hand-built plan uses, so the server needs nothing new. A plan that
 * cannot also clear the debts gets no twin.
 */
function clearDebtTwins(factFind: DraftFactFind, candidates: CashflowPensionCandidate[], sources: Array<{ type: string; id: string; value: number }>): CashflowPensionCandidate[] {
    // Which debts: strategy/debtsToTest.ts (any interest-bearing debt, whatever its size; interest-free debts from
    // CP_DEBT_TWIN_FLOOR; a card paid off monthly is never listed), the rule every module uses (26 Oct 2026).
    const debts = debtsWorthTesting(payableDebtsForCashflow(factFind));
    const total = debts.reduce((t, d) => t + d.balance, 0);
    if (debts.length === 0) return [];
    const reserve = cashflowReserveOf(factFind);
    const twins: CashflowPensionCandidate[] = [];
    for (const c of candidates) {
        if (c.debtPayoff) continue;
        // What this plan leaves of each account, with the emergency money held back in the bank.
        let keep = reserve;
        const left = sources.map((s) => {
            const taken = (c.fundingSources || []).filter((f) => f.type === s.type && f.id === s.id).reduce((t, f) => t + f.amountToTake, 0);
            let value = Math.max(0, s.value - taken);
            if (s.type === 'bankAccounts' && keep > 0) { const held = Math.min(keep, value); value -= held; keep -= held; }
            return { ...s, value };
        }).filter((s) => s.value > 0);
        if (keep > 0) {
            // The emergency money could not all stay in the bank: hold the rest back from the other accounts too.
            for (const s of left) { const held = Math.min(keep, s.value); s.value -= held; keep -= held; }
        }
        if (left.reduce((t, s) => t + s.value, 0) + 0.5 < total) continue;
        const debtPayoff = manualDebtPayoff(factFind, total, left.filter((s) => s.value > 0) as never);
        if (!debtPayoff) continue;
        twins.push(c.changeNothing
            ? { id: 'cp-clear-debt', name: 'Pay off the debts', description: `Pay off ${formatCurrency(total)} of debts from savings, dearest first, keeping the emergency money in the bank.`, debtPayoff }
            : { ...c, id: `${c.id}-clear-debt`, name: `${c.name}, and clear the debts`, description: `${c.description} Then pay off ${formatCurrency(total)} of debts from what is left, keeping the emergency money in the bank.`, debtPayoff });
    }
    return twins;
}
